import { useSearchParams } from '@solidjs/router';
import { createMemo, type JSX, Show } from 'solid-js';
import { EmptyState } from '../components/EmptyState';
import { FleetQueuePanel } from '../components/FleetQueuePanel';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { useTaskQueue } from '../lib/fleet';
import { type MachineNarrow, machineNarrow, tasksPath } from '../lib/fleet-work';
import { useFleetUtilization } from '../lib/fleetUtilization';
import { canWriteTasks, roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';

// The Fleet redesign's WORK page (`/fleet/work`, #6788, owner ruling G12): the queue
// board the /fleet page used to carry, moved UNCHANGED onto its own screen: New task,
// Stop, the Details drawer, View plan / View run, queue positions and paging. /fleet
// keeps a read-only head of it ("Work in progress") with "See all work" into here.
//
// THE QUEUE IS LIVE (#7612): `useTaskQueue` re-reads `GET /v1/tasks` when the account
// stream's `tasks` topic says a task moved — the board is always the server's pages,
// never stream rows painted over them, so it pages honestly behind Load more.
//
// `?machine=<runner_id>` NARROWS IT ON THE SERVER (`GET /v1/tasks?filter=runner_id:
// eq:<id>`, #6558), never on the client: a client filter over the rows held would
// silently drop a machine's older work. The narrowed board is the same live read at
// its own path — a task moving may be the machine's, so it re-reads too. A non-uuid
// id would 400 at the api, so it reads nothing and shows the same "No work for this
// machine." an unknown machine's empty answer shows, with the way back to all work.
// The narrow lives in the URL (ruling G10), so a reload or a shared link keeps it.
//
// ROLES (G12): New task and Stop are `write:tasks`, which members hold; only a
// viewer loses them (`canWriteTasks`), on a role that has answered (`roleSettled`).
//
// `token` is injectable for tests (mirrors Fleet / Home); the router mounts this
// with no props -> dev session.

/** The board for one narrow. Mounted per narrow, so a new narrow re-reads from page one. */
function WorkBoard(props: {
  readonly token: string | null;
  readonly narrow: Exclude<MachineNarrow, { readonly kind: 'invalid' }>;
  /** The narrowed machine's display name, once the utilization read is in. */
  readonly machineName: () => string | undefined;
  readonly canWrite: boolean;
  readonly onClearMachine: () => void;
}): JSX.Element {
  const tasks = useTaskQueue(props.token, tasksPath(props.narrow) ?? undefined);
  const machine = props.narrow.kind === 'machine' ? props.narrow : null;
  return (
    <FleetQueuePanel
      list={tasks}
      token={props.token}
      machine={
        machine === null
          ? null
          : (() => {
              const name = props.machineName();
              return name === undefined ? { id: machine.id } : { id: machine.id, name };
            })()
      }
      onClearMachine={props.onClearMachine}
      canWrite={props.canWrite}
    />
  );
}

export function FleetWork(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const me = useMe(token);
  const canWrite = (): boolean => roleSettled(me) && canWriteTasks(me.data?.role);
  // The narrow lives in the URL, so `setParams`, not a local signal, clears it: a
  // clear that only reset a signal would leave the URL claiming a filter.
  const [params, setParams] = useSearchParams<{ machine?: string }>();
  const narrow = createMemo(() => machineNarrow(params.machine));
  const clearMachine = (): void => setParams({ machine: undefined }, { replace: true });
  // The machine's name for the narrow's line, off the one utilization read.
  const utilization = useFleetUtilization(token);
  const machineName = (id: string): string | undefined =>
    utilization.data?.boxes.find((box) => box.id === id)?.display_name;
  // One key per narrow: `<Show keyed>` remounts the board when it changes.
  const boardKey = (): string => {
    const n = narrow();
    return n.kind === 'machine' ? `machine:${n.id}` : n.kind;
  };

  const hasSession = token !== null;
  // The page's title block, OUTSIDE the session Show: every route names itself
  // through `<PageHeader/>` (the 390px lane treats a route with no `<h1>` as a
  // finding), its signed-out state included. The eyebrow is the live dot: the
  // board below is live (`tasks` topic).
  return (
    <>
      <PageHeader
        title={t('fleet.work.title')}
        desc={t('fleet.work.subtitle')}
        eyebrow={t('app.live')}
      />
      <Show when={hasSession} fallback={<EmptyState message={t('fleet.no_session')} />}>
        <Show
          when={boardKey() !== 'invalid' && boardKey()}
          keyed
          fallback={
            <Panel title={t('fleet.queue_heading')}>
              <EmptyState message={t('fleet.queue_machine_empty')} />
              <p class="panel__footer">
                <Button variant="ghost" size="sm" onClick={clearMachine}>
                  {t('fleet.queue_machine_clear')}
                </Button>
              </p>
            </Panel>
          }
        >
          {(_key) => {
            const n = narrow();
            return n.kind === 'invalid' ? null : (
              <WorkBoard
                token={token}
                narrow={n}
                machineName={() => (n.kind === 'machine' ? machineName(n.id) : undefined)}
                canWrite={canWrite()}
                onClearMachine={clearMachine}
              />
            );
          }}
        </Show>
      </Show>
    </>
  );
}
